import type { TimeValue } from "./types";

// zero-dep 시각 유틸(RFC-time-input). 시각을 분(0–1439)으로 환산해 clamp/비교한다.

/** 시각 → 자정 기준 분(0–1439). */
export const toMinutes = (t: TimeValue): number => t.hours * 60 + t.minutes;

/** 분(정수) → 시각. 24h 범위로 정규화(음수/초과는 하루 안으로 wrap하지 않고 clamp는 호출측). */
export const fromMinutes = (m: number): TimeValue => ({
  hours: Math.floor(m / 60),
  minutes: m % 60,
});

const clampInt = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(n)));

/** hours 0–23, minutes 0–59로 clamp. */
export const clampTime = (t: TimeValue): TimeValue => ({
  hours: clampInt(t.hours, 0, 23),
  minutes: clampInt(t.minutes, 0, 59),
});

/** 분을 step으로 정규화(내림). step<=1이면 그대로. */
export const snapMinuteToStep = (minutes: number, step: number): number =>
  step > 1 ? Math.floor(minutes / step) * step : minutes;

/** min/max 범위로 clamp(분 기준). */
export const clampToRange = (t: TimeValue, min?: TimeValue, max?: TimeValue): TimeValue => {
  let m = toMinutes(clampTime(t));
  if (min) m = Math.max(m, toMinutes(min));
  if (max) m = Math.min(m, toMinutes(max));
  return fromMinutes(m);
};

/** clamp + step 정규화 통합(커밋 직전 사용). */
export const normalizeTime = (
  t: TimeValue,
  { min, max, minuteStep = 1 }: { min?: TimeValue; max?: TimeValue; minuteStep?: number },
): TimeValue => {
  const clamped = clampTime(t);
  const snapped: TimeValue = {
    hours: clamped.hours,
    minutes: snapMinuteToStep(clamped.minutes, minuteStep),
  };
  return clampToRange(snapped, min, max);
};

/** "HH:MM"(0-패딩). 트리거/표시용. */
export const formatTime = (t: TimeValue): string =>
  `${String(t.hours).padStart(2, "0")}:${String(t.minutes).padStart(2, "0")}`;
